// src/App.js
import { useState, useEffect } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { getEmployees, getAllAttendance, getUsers } from "./firebase/services";

import Login         from "./pages/Login";
import Dashboard     from "./pages/Dashboard";
import Employees     from "./pages/Employees";
import Attendance    from "./pages/Attendance";
import UserManagement from "./pages/UserManagement";
import Sidebar       from "./components/Sidebar";
import { Spinner }   from "./components/UI";

// ── CSS variables + global styles ─────────────────────────────────────────────
const GlobalStyle = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,300&display=swap');
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    :root {
      --ink: #0d0d0d; --paper: #f5f2eb; --cream: #ede9df;
      --rust: #b84c27; --rust-light: #e8a07a;
      --slate: #3a4a5c; --slate-light: #6b7f93;
      --success: #2d7a4f; --warn: #b87a00; --danger: #b82727;
    }
    html, body, #root { height: 100%; font-family: 'DM Sans', sans-serif; background: var(--paper); color: var(--ink); }
    h1, h2, h3, h4, h5 { font-family: 'Syne', sans-serif; }
    input, select, textarea, button { font-family: inherit; }
    ::-webkit-scrollbar { width: 6px; height: 6px; }
    ::-webkit-scrollbar-track { background: var(--cream); }
    ::-webkit-scrollbar-thumb { background: var(--slate-light); border-radius: 3px; }
    @keyframes fadeUp  { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes spin    { to { transform: rotate(360deg); } }
    @keyframes slideIn { from { opacity: 0; transform: translateX(-16px); } to { opacity: 1; transform: translateX(0); } }
  `}</style>
);

// ── Loading screen ─────────────────────────────────────────────────────────────
const LoadingScreen = () => (
  <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16, background: "var(--paper)" }}>
    <Spinner size={36} />
    <div style={{ fontSize: 14, color: "var(--slate-light)" }}>Loading portal…</div>
  </div>
);

// ── Protected layout ───────────────────────────────────────────────────────────
function PortalApp() {
  const { user, loading, isAdmin } = useAuth();
  const [active, setActive]         = useState("dashboard");
  const [employees, setEmployees]   = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [users, setUsers]           = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      setDataLoading(true);
      try {
        const [emps, att] = await Promise.all([getEmployees(), getAllAttendance()]);
        setEmployees(emps);
        setAttendance(att);
        if (isAdmin) {
          const u = await getUsers();
          setUsers(u);
        }
      } catch (e) {
        console.error("Data load error:", e);
      } finally {
        setDataLoading(false);
      }
    })();
  }, [user, isAdmin]);

  if (loading) return <LoadingScreen />;
  if (!user)   return <Login />;

  if (dataLoading) return (
    <>
      <GlobalStyle />
      <div style={{ display: "flex" }}>
        <Sidebar active={active} setActive={setActive} />
        <main style={{ marginLeft: 230, flex: 1, padding: "80px 36px", display: "flex", alignItems: "flex-start", justifyContent: "center" }}>
          <div style={{ textAlign: "center" }}>
            <Spinner size={32} />
            <div style={{ marginTop: 12, fontSize: 14, color: "var(--slate-light)" }}>Loading data from Firebase…</div>
          </div>
        </main>
      </div>
    </>
  );

  const sharedProps = { employees, setEmployees, attendance, setAttendance };

  const renderPage = () => {
    switch (active) {
      case "dashboard":  return <Dashboard {...sharedProps} />;
      case "employees":  return <Employees {...sharedProps} />;
      case "current":    return <Employees {...sharedProps} filterStatus="current" />;
      case "past":       return <Employees {...sharedProps} filterStatus="past" />;
      case "attendance": return <Attendance {...sharedProps} />;
      case "users":      return isAdmin ? <UserManagement users={users} setUsers={setUsers} /> : <Dashboard {...sharedProps} />;
      default:           return <Dashboard {...sharedProps} />;
    }
  };

  return (
    <>
      <GlobalStyle />
      <div style={{ display: "flex", minHeight: "100vh" }}>
        <Sidebar active={active} setActive={setActive} />
        <main style={{ marginLeft: 230, flex: 1, padding: "36px 36px 60px", minHeight: "100vh", maxWidth: "calc(100vw - 230px)", overflowX: "hidden" }}>
          {renderPage()}
        </main>
      </div>
    </>
  );
}

// ── Root export ────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <PortalApp />
    </AuthProvider>
  );
}
